import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const emptyEvaluation = {
  ouverture_classe_id: '',
  matiere_id: '',
  periode_id: '',
  type_evaluation_id: '',
  date_evaluation: '',
  pj_devoirs: null
};

const EvaluationModal = ({
  isOpen,
  onClose,
  onSaved,
  editing,
  ouvertures,
  matieres,
  periodes,
  types,
  defaultOuvertureClasseId
}) => {
  const [form, setForm] = useState(emptyEvaluation);

  useEffect(() => {
    if (editing) {
      setForm({
        ouverture_classe_id: editing.ouverture_classe_id || '',
        matiere_id: editing.matiere_id || '',
        periode_id: editing.periode_id || '',
        type_evaluation_id: editing.type_evaluation_id || '',
        date_evaluation: editing.date_evaluation || '',
        pj_devoirs: null // Le fichier existant ne peut pas être rechargé dans un input file
      });
    } else if (defaultOuvertureClasseId) {
      setForm({ ...emptyEvaluation, ouverture_classe_id: defaultOuvertureClasseId });
    } else {
      setForm(emptyEvaluation);
    }
  }, [editing, defaultOuvertureClasseId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est un PDF
      if (file.type !== 'application/pdf') {
        Swal.fire({ 
          icon: 'error', 
          title: 'Erreur', 
          text: 'Seuls les fichiers PDF sont acceptés.' 
        });
        e.target.value = ''; // Reset l'input
        return;
      }
      // Vérifier la taille (limite : 10MB)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({ 
          icon: 'error', 
          title: 'Erreur', 
          text: 'Le fichier ne doit pas dépasser 10MB.' 
        });
        e.target.value = ''; // Reset l'input
        return;
      }
    }
    setForm({ ...form, pj_devoirs: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ouverture_classe_id || !form.matiere_id || !form.periode_id || !form.type_evaluation_id || !form.date_evaluation) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'Tous les champs obligatoires doivent être remplis.' });
      return;
    }

    // Créer FormData pour l'upload de fichier
    const formData = new FormData();
    formData.append('ouverture_classe_id', form.ouverture_classe_id);
    formData.append('matiere_id', form.matiere_id);
    formData.append('periode_id', form.periode_id);
    formData.append('type_evaluation_id', form.type_evaluation_id);
    formData.append('date_evaluation', form.date_evaluation);
    
    if (form.pj_devoirs) {
      formData.append('pj_devoirs', form.pj_devoirs);
    }

    await onSaved(formData, editing);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Évaluation"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          maxWidth: '700px',
          width: '100%',
          margin: 'auto',
          height: '400px',
          borderRadius: 12,
          overflow: 'hidden',
          padding: 0
        }
      }}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} évaluation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Classe ouverte *</label>
            {defaultOuvertureClasseId ? (
              (() => {
                const ouverture = ouvertures.find(o => (o.ouverture_classe_id || o.id).toString() === defaultOuvertureClasseId.toString());
                const libelle = ouverture ? `${ouverture.classe?.classe || ouverture.classe_id} (${ouverture.annee_scolaire?.annee_scolaire || ''})` : defaultOuvertureClasseId;
                return (
                  <div className="inline-block px-3 py-1 rounded bg-blue-100 text-blue-800 font-semibold text-sm mt-1">
                    {libelle}
                  </div>
                );
              })()
            ) : (
              <select
                name="ouverture_classe_id"
                value={form.ouverture_classe_id}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">Sélectionner...</option>
                {ouvertures.map((o) => (
                  <option key={o.ouverture_classe_id || o.id} value={o.ouverture_classe_id || o.id}>
                    {o.classe?.classe || o.classe_id}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Matière *</label>
            <select
              name="matiere_id"
              value={form.matiere_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {matieres.map((m) => (
                <option key={m.id} value={m.id}>{m.lib_matiere}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Période *</label>
            <select
              name="periode_id"
              value={form.periode_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {periodes.map((p) => (
                <option key={p.id} value={p.id}>{p.periode}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type *</label>
            <select
              name="type_evaluation_id"
              value={form.type_evaluation_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.type_evaluation}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date *</label>
            <input
              type="date"
              name="date_evaluation"
              value={form.date_evaluation}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Pièce jointe (Devoir PDF)
              {editing && editing.pj_devoirs && (
                <span className="text-sm text-blue-600 ml-2">
                  (Fichier actuel: {editing.pj_devoirs})
                </span>
              )}
            </label>
            <input
              type="file"
              name="pj_devoirs"
              onChange={handleFileChange}
              className="w-full border rounded px-3 py-2"
              accept=".pdf"
            />
            <div className="text-xs text-gray-500 mt-1">
              Format accepté: PDF uniquement (max 10MB)
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
          <button type="submit" className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">{editing ? 'Enregistrer' : 'Créer'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default EvaluationModal;
